// BHP-1050: topbar account dropdown — links to the account page and logs out.
// Replaces the placeholder account icon + separate logout button.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { AccountIcon, ChevronDownIcon, LogoutIcon } from './icons';

export default function AccountMenu({
  name,
  email
}: {
  name: string;
  email: string | null;
}): JSX.Element {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onDocEvent(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') {
          setOpen(false);
        }
        return;
      }
      if (ref.current !== null && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocEvent);
    document.addEventListener('keydown', onDocEvent);
    return () => {
      document.removeEventListener('mousedown', onDocEvent);
      document.removeEventListener('keydown', onDocEvent);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="flex items-center gap-2 rounded border-0 bg-transparent p-2 text-ink-muted hover:bg-surface-muted hover:text-ink"
      >
        <AccountIcon className="h-5 w-5" />
        <span className="hidden text-sm font-medium sm:inline">{name}</span>
        <ChevronDownIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-solid border-ink-muted/20 bg-surface shadow-lg"
        >
          {/* BHP-1095: identity header — name + email; separated from the items
              by a subtle background tint (no divider line). */}
          <div className="bg-surface-muted px-3 py-2">
            <div className="truncate text-sm font-semibold text-ink">
              {name}
            </div>
            {email !== null && email !== '' ? (
              <div className="truncate text-xs text-ink-muted">{email}</div>
            ) : null}
          </div>
          <Link
            role="menuitem"
            to="/account/change"
            onClick={() => {
              setOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-muted"
          >
            <AccountIcon className="h-4 w-4" />
            Account
          </Link>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              void auth.signoutRedirect();
            }}
            className="flex w-full items-center gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm text-ink hover:bg-surface-muted"
          >
            <LogoutIcon className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
